import { attach, createEvent, type EffectParams, sample } from 'effector'

import { getRegisterInfo } from '@portal/client-info'

import { portalProfileAPI } from '@/entities/profile/portal'
import { AcceptTermsDialog } from '@/features/terms'
import { createFacebookSDK } from '@/shared/lib/facebook-sdk'
import { $lang } from '@/shared/lib/i18n'
import { navigateTo } from '@/shared/lib/next'
import { toast } from '@/shared/lib/toast'
import { type NotificationArgs, showNotificationFx } from '@/shared/ui'
import { modal } from '@/shared/ui/modal'

export const facebookLogin = createEvent()
const facebookAuthorized = createEvent<facebook.AuthResponse>()

const facebookSDK = createFacebookSDK()
const facebookLoginFx = attach({ effect: portalProfileAPI.facebookLogin })

// --- terms ---

export const showAcceptTermsDialogFx = attach({
    effect: AcceptTermsDialog.model.showFx,
    mapParams: () => ({ onAcceptButtonClick: facebookLogin }),
})

// --- init ---

sample({
    clock: facebookLogin,
    filter: facebookSDK.initialized,
    target: facebookSDK.getLoginStatus,
})

// --- status ---

// status success

sample({
    clock: facebookSDK.getLoginStatus.doneData,
    fn: (response) => response.authResponse,
    target: facebookAuthorized,
})

// status failure

sample({
    clock: facebookSDK.getLoginStatus.fail,
    target: facebookSDK.login,
})

// --- login ---

// login success

sample({
    clock: facebookSDK.login.doneData,
    fn: (response) => response.authResponse,
    target: facebookAuthorized,
})

// login failure

sample({
    clock: facebookSDK.login.failData,
    filter: () => process.env.NODE_ENV !== 'production',
    // TODO
    // target: fbLoginOAuth,
    fn: (response) => ({ message: `facebook login failed: status '${response.status}'` }),
    target: toast.errorFx,
})

// --- play login ---

sample({
    clock: facebookAuthorized,
    source: $lang,
    fn: (lang, response): EffectParams<typeof facebookLoginFx> => ({
        access_token: response.accessToken ?? '',
        register_info: getRegisterInfo(lang),
    }),
    target: facebookLoginFx,
})

// play login success

sample({
    clock: facebookLoginFx.doneData,
    target: [modal.closeAll, navigateTo('/play', { replace: true })],
})

// play login failure

sample({
    clock: facebookLoginFx.fail,
    fn: (): NotificationArgs => ({ notification: 'error_default' }),
    target: showNotificationFx,
})
